"use client";

import EngineeringIcon from '@mui/icons-material/Engineering';
import { cn } from "../lib/utils";
import { Sparkles } from "lucide-react";
import { useLanguage } from '../contexts/LanguageContext';
import CurrencyExchangeOutlinedIcon from '@mui/icons-material/CurrencyExchangeOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import DeveloperBoardOutlinedIcon from '@mui/icons-material/DeveloperBoardOutlined';
import Groups3OutlinedIcon from '@mui/icons-material/Groups3Outlined';
import PlagiarismOutlinedIcon from '@mui/icons-material/PlagiarismOutlined';
import { useEffect, useState } from 'react';

interface DisplayCardProps {
    className?: string;
    icon?: React.ReactNode;
    title?: string;
    description?: string;
    date?: string;
    iconClassName?: string;
    titleClassName?: string;
    hasLogo?: boolean;
    cardWidth?: number;
}

function DisplayCard({
    className,
    icon = <Sparkles className="size-4 text-blue-300" />,
    title = "Featured",
    description = "Discover amazing content",
    date = "Just now",
    iconClassName = "text-blue-500",
    titleClassName = "text-white",
    hasLogo = false,
    cardWidth = 22,
}: DisplayCardProps) {
    return (
        <div
            className={cn(
                `hidden relative md:flex h-36 -skew-y-[8deg] select-none flex-col justify-between rounded-xl border-2 backdrop-blur-sm px-4 py-3 transition-all duration-700 after:absolute after:-right-1 after:top-[-5%] after:h-[110%] after:w-[${cardWidth}rem] after:bg-gradient-to-l after:from-background after:to-transparent after:content-[''] hover:border-white/20 [&>*]:flex [&>*]:items-center [&>*]:gap-2 group`,
                className
            )}
            style={{ 
                width: `${cardWidth}rem`,
                backgroundColor: 'rgba(36, 68, 115, 0.7)' // #244473 with 70% opacity
            }}
        >
            <div>
                <span className="relative inline-block ">
                    {icon}
                </span>
                <p className={cn("text-xs md:text-lg font-medium", titleClassName)}>{title}</p>
            </div>
            {hasLogo ? (
                <div className='flex justify-around items-center '>
                    <div>
                        <img src="/images/logo.png" alt="Moktamel" height={80} width={96} className='pt-2' />
                    </div>
                    <div>
                        <p className="text-white text-xs md:text-lg">{description}</p>

                    </div>
                </div>
            ) : (
                <p className="group-hover:text-white text-lg h-full">{description}</p>
            )
            }
        </div>
    );
}

interface DisplayCardsProps {
    cards?: DisplayCardProps[];
}

export default function DisplayCards({ cards }: DisplayCardsProps) {
    const { t } = useLanguage();
    const [cardWidth, setCardWidth] = useState(22);

    useEffect(() => {
        const calCardWidth = () => {
            const screenWidth = window.innerWidth;
            const cardWidthRatio = screenWidth / 1440;
            const cardWidth = (22 * cardWidthRatio);
            const minCardWidth = 16.1;
            const clampedCardWidth = Math.max(minCardWidth, cardWidth);
            setCardWidth(clampedCardWidth);
        };
        calCardWidth();
        window.addEventListener('resize', calCardWidth);
        return () => window.removeEventListener('resize', calCardWidth);
    }, []);

    const defaultCards = [
        {
            className: "[grid-area:stack] hover:-translate-y-[70%] before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-[#244473]/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration:700 hover:grayscale-0 before:left-0 before:top-0",
            icon: <EngineeringIcon className="text-blue-500" />,
            title: t('home.who.cards.card1.title'),
            description: t('home.who.cards.card1.subtitle'),
            cardWidth: cardWidth,
        },
        {
            className: "[grid-area:stack] translate-x-8 translate-y-[20%] hover:-translate-y-[60%] before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-[#244473]/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration:700 hover:grayscale-0 before:left-0 before:top-0",
            icon: <CurrencyExchangeOutlinedIcon className="text-blue-500" />,
            title: t('home.who.cards.card2.title'),
            description: t('home.who.cards.card2.subtitle'),
            cardWidth: cardWidth,
        },
        {
            className: "[grid-area:stack] translate-x-16 translate-y-[40%] hover:-translate-y-[40%] before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-[#244473]/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration:700 hover:grayscale-0 before:left-0 before:top-0",
            icon: <DeveloperBoardOutlinedIcon className="text-blue-500" />,
            title: t('home.who.cards.card3.title'),
            description: t('home.who.cards.card3.subtitle'),
            cardWidth: cardWidth,
        },
        {
            className: "[grid-area:stack] translate-x-24 translate-y-[60%] hover:-translate-y-[20%] before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-[#244473]/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration:700 hover:grayscale-0 before:left-0 before:top-0",
            icon: <Groups3OutlinedIcon className="text-blue-500" />,
            title: t('home.who.cards.card4.title'),
            description: t('home.who.cards.card4.subtitle'),
            cardWidth: cardWidth,
        },
        {
            className: "[grid-area:stack] translate-x-32 translate-y-[80%] hover:-translate-y-1 before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-[#244473]/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration:700 hover:grayscale-0 before:left-0 before:top-0",
            icon: <PlagiarismOutlinedIcon className="text-blue-500" />,
            title: t('home.who.cards.card5.title'),
            description: t('home.who.cards.card5.subtitle'),
            cardWidth: cardWidth,
        },
        {
            className: "[grid-area:stack] translate-x-40 translate-y-[100%] hover:translate-y-[19%] before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-[#244473]/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration:700 hover:grayscale-0 before:left-0 before:top-0",
            icon: <PeopleOutlineOutlinedIcon className="text-blue-500" />,
            title: t('home.who.cards.card6.title'),
            description: t('home.who.cards.card6.subtitle'),
            cardWidth: cardWidth,
        },
        {
            className: "[grid-area:stack] translate-x-48 translate-y-[120%] ",
            cardWidth: cardWidth,
            title: t('home.who.cards.card7.title'),
            description: t('home.who.cards.card7.subtitle'),
            hasLogo: true,
        },
    ];

    const displayCards = cards || defaultCards;

    return (
        <div className="grid [grid-template-areas:'stack'] place-items-center opacity-100 animate-in fade-in-0 duration-700">
            {displayCards.map((cardProps, index) => (
                <DisplayCard key={index} {...cardProps} />
            ))}
        </div>
    );
}